import type { HTMLAttributes, Ref } from 'react';
import { memo, useEffect, useMemo, useRef } from 'react';

import { withErrorBoundary } from '@shared/ErrorBoundary';
import { useJwPlayer } from '@shared/queries/common';
import { omit } from '@utilities/collection-utils';
import { createRandomUUID } from '@utilities/random';

export type JwPlayerProps = {
  configuration: jwplayer.SetupConfig;
  ref?: Ref<jwplayer.JWPlayer>;
};

const JwPlayer = (props: JwPlayerProps & HTMLAttributes<HTMLDivElement>) => {
  const { configuration, ref } = props;
  const target = useRef<HTMLDivElement>(null);
  const player = useRef<jwplayer.JWPlayer>(null);
  const id = props.id || `jwplayer-${createRandomUUID}`;

  const { data: jwplayer } = useJwPlayer();

  useEffect(() => {
    if (!jwplayer || !target.current || player.current) return;

    player.current = jwplayer(target.current).setup(configuration);

    if (typeof ref === 'function') ref(player.current);
    else if (ref) ref.current = player.current;
  }, [jwplayer, configuration, ref]);

  useEffect(() => {
    return () => {
      player.current?.remove();
      player.current = null;
    };
  }, []);

  const divProps = useMemo(() => omit(props, 'configuration', 'ref'), [props]);

  return <div {...divProps} id={id} ref={target} />;
};

export default memo(withErrorBoundary(JwPlayer));
